/**
 * telemetry.js — Application Insights integration (inert by default)
 *
 * Rules:
 * - Cookie-less (GDPR: no consent banner needed).
 * - Fully inert when the backend returns no connectionString.
 * - tenant_id is an opaque id — never set names/emails as user context.
 * - All exported functions are safe no-ops before init / when unconfigured.
 */

let _ai = null;

/**
 * Fetch the connection string from the backend and, if present, initialise the
 * Application Insights JS SDK.  Idempotent — calling it twice returns the same
 * instance without re-fetching.
 *
 * @returns {Promise<object|null>} The ApplicationInsights instance, or null if
 *   the backend has no connection string configured.
 */
export async function initTelemetry() {
  if (_ai) return _ai;
  try {
    const token = localStorage.getItem("token");
    const res = await fetch("/api/config/telemetry", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const { connectionString } = await res.json();
    if (!connectionString) return null;

    // Dynamic import keeps the SDK out of the initial bundle when unconfigured.
    const { ApplicationInsights } = await import("@microsoft/applicationinsights-web");
    _ai = new ApplicationInsights({
      config: {
        connectionString,
        disableCookiesUsage: true,
        enableAutoRouteTracking: true,
      },
    });
    _ai.loadAppInsights();
    _ai.trackPageView();
    return _ai;
  } catch {
    // Never throw — telemetry must never break the app.
    return null;
  }
}

/**
 * Attach a telemetry initializer that stamps every envelope with tenant_id (and
 * optionally user_role).  Call once after the user object is resolved.
 *
 * @param {string} tenantId  Opaque tenant identifier (e.g. "elementar_demo").
 * @param {string} [role]    User role string (e.g. "client").
 */
export function setTelemetryTenant(tenantId, role) {
  if (!_ai || !tenantId) return;
  _ai.addTelemetryInitializer((envelope) => {
    envelope.data = envelope.data || {};
    envelope.data.tenant_id = tenantId;
    if (role) envelope.data.user_role = role;
  });
}

/**
 * Fire a named custom event.  Safe no-op before init or when unconfigured.
 *
 * @param {string} name              Event name (e.g. "document_view").
 * @param {Object} [properties={}]   Additional dimensions (no PII).
 */
export function trackEvent(name, properties = {}) {
  try {
    _ai?.trackEvent({ name }, properties);
  } catch {
    // Swallow — telemetry must never surface errors to the user.
  }
}

/** Exposed for unit tests only — resets module state. */
export function __resetTelemetryState() {
  _ai = null;
}
